import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import Badge from './Badge';
import DocumentWallet from './DocumentWallet';
import AuditLog from './AuditLog';
import EntityLink from './EntityLink';
import NoteText, { InvoiceNotes } from './NoteText';
import VoidArInvoiceButton from './VoidArInvoiceButton';
import { useAuth } from '../context/AuthContext';
import ConsultingLines from './ConsultingLines';
import ConsultingInvoiceModal from './ConsultingInvoiceModal';

export default function ARInvoicePanel({ invoiceId, onClose, onChanged }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [showConsultingEdit, setShowConsultingEdit] = useState(false);
  const isConsulting = data?.invoice_type === 'consulting';
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [showPayForm, setShowPayForm] = useState(false);
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const EMPTY_PAY_FORM = { payment_date: '', cash_amount: '', epd_rate: '', epd_amount: '', bgc_amount: '', reference: '', batch_ref: '', notes: '' };
  const [payForm, setPayForm] = useState(EMPTY_PAY_FORM);
  const [saving, setSaving] = useState(false);

  function load() {
    api.get(`/ar-invoices/${invoiceId}`).then(r => setData(r.data)).catch(() => setData(null));
  }

  useEffect(() => { load(); }, [invoiceId]);

  async function savePayment() {
    setSaving(true);
    try {
      const payload = {
        ...payForm,
        cash_amount: Number(payForm.cash_amount || 0),
        epd_rate: payForm.epd_rate ? Number(payForm.epd_rate) : null,
        epd_amount: payForm.epd_amount ? Number(payForm.epd_amount) : 0,
        bgc_amount: payForm.bgc_amount ? Number(payForm.bgc_amount) : 0,
      };
      if (editingPaymentId) {
        await api.put(`/ar-invoices/${invoiceId}/payments/${editingPaymentId}`, payload);
      } else {
        await api.post(`/ar-invoices/${invoiceId}/payments`, payload);
      }
      setPayForm(EMPTY_PAY_FORM);
      setEditingPaymentId(null);
      setShowPayForm(false);
      load();
      if (onChanged) onChanged();
    } finally { setSaving(false); }
  }

  function startEditPayment(p) {
    setPayForm({
      payment_date: p.payment_date || '',
      cash_amount: p.cash_amount ?? '',
      epd_rate: p.epd_rate ?? '',
      epd_amount: p.epd_amount ?? '',
      bgc_amount: p.bgc_amount ?? '',
      reference: p.reference || '',
      batch_ref: p.batch_ref || '',
      notes: p.notes || '',
    });
    setEditingPaymentId(p.id);
    setShowPayForm(true);
  }

  async function setVerification(paymentId, status) {
    await api.put(`/ar-invoices/${invoiceId}/payments/${paymentId}/verify`, { verification_status: status });
    load();
    if (onChanged) onChanged();
  }

  function startEdit() {
    setEditForm({ ...data });
    setIsEditing(true);
    setEditError('');
  }

  async function saveEdit() {
    if (!editForm.invoice_number?.trim()) { setEditError('Invoice number required'); return; }
    setEditSaving(true);
    setEditError('');
    try {
      await api.put(`/ar-invoices/${invoiceId}`, editForm);
      setIsEditing(false);
      setEditForm(null);
      load();
      if (onChanged) onChanged();
    } catch (e) {
      setEditError('Save failed: ' + (e.response?.data?.error || e.message));
    } finally { setEditSaving(false); }
  }

  function cancelEdit() {
    setIsEditing(false);
    setEditForm(null);
    setEditError('');
  }

  return (
    <>
      <div className="panel-overlay" onClick={onClose} />
      <div className="quick-panel" style={{ width: 500 }}>
        <div className="drawer-header">
          <h3 className="drawer-title">{isEditing ? 'Edit Invoice' : (data?.invoice_number || 'Loading…')}</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            {!isEditing && user?.role === 'admin' && (
              <button className="btn btn-sm btn-secondary" onClick={() => (isConsulting ? setShowConsultingEdit(true) : startEdit())}>✏️ Edit</button>
            )}
            {!isEditing && user?.role === 'admin' && data && (
              <VoidArInvoiceButton invoice={{ ...data, id: invoiceId }} onVoided={() => { load(); if (onChanged) onChanged(); }} />
            )}
            <button className="btn btn-sm btn-secondary" onClick={() => navigate(`/ar-invoices/${invoiceId}`)}>
              View Full Page ↗
            </button>
            <button className="drawer-close" onClick={onClose}>×</button>
          </div>
        </div>

        {data && !isEditing && (
          <div className="panel-body">
            <div className="panel-summary-line">
              <Badge>{data.status}</Badge>
              <Badge variant={data.invoice_type === 'passthrough' ? 'info' : isConsulting ? 'success' : undefined}>
                {data.invoice_type === 'passthrough' ? 'Allowance / Reimbursement' : isConsulting ? 'Business Consulting' : 'Standard'}
              </Badge>
              {isConsulting ? data.client_name : <EntityLink type="contractor" id={data.contractor_id}>{data.contractor_name}</EntityLink>}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, marginBottom: 16 }}>
              <DetailRow label="Invoice #" value={data.invoice_number} />
              <DetailRow label="Client" value={<EntityLink type="client" id={data.client_id}>{data.client_name}</EntityLink>} />
              {!isConsulting && <DetailRow label="Contractor" value={<EntityLink type="contractor" id={data.contractor_id}>{data.contractor_name}</EntityLink>} />}
              {isConsulting && <DetailRow label="Work Order" value={<EntityLink type="work_order" id={data.wo_id}>{data.client_wo_number}</EntityLink>} />}
              {isConsulting && data.po_number && <DetailRow label="Client PO" value={<EntityLink type="purchase_order" id={data.po_id}>{data.po_number}</EntityLink>} />}
              <DetailRow label="Invoice Date" value={data.invoice_date} />
              <DetailRow label="Period" value={data.period_start && data.period_end ? `${data.period_start} – ${data.period_end}` : null} />
              {!isConsulting && <DetailRow label="Hours Billed" value={data.hours_billed} />}
              {!isConsulting && <DetailRow label="Hours Approved" value={data.hours} />}
              {!isConsulting && <DetailRow label="Rate" value={data.rate != null ? `$${data.rate}/hr` : null} />}
              <DetailRow label="Due Date" value={data.due_date} />
            </div>
            {isConsulting && <ConsultingLines lines={data.lines || []} invoice={data} />}
            {showConsultingEdit && (
              <ConsultingInvoiceModal invoice={{ ...data, id: invoiceId }} onClose={() => setShowConsultingEdit(false)}
                onSaved={() => { setShowConsultingEdit(false); load(); if (onChanged) onChanged(); }} />
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
              <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12 }}>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>Invoice Amount</div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>${data.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
              </div>
              <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12 }}>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>Outstanding</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: data.summary.outstanding > 0 ? 'var(--warning)' : 'var(--success)' }}>
                  ${data.summary.outstanding.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </div>
              </div>
            </div>

            <InvoiceNotes notes={data.notes} />

            <h4 style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Payments</h4>
            {data.payments.length === 0 && <div className="form-hint" style={{ marginBottom: 12 }}>No payments recorded yet.</div>}
            {data.payments.map(p => (
              <div key={p.id} style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 10, marginBottom: 8, fontSize: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <strong>${p.cash_amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
                  <Badge variant={p.verification_status === 'verified' ? 'success' : 'warning'}>{p.verification_status}</Badge>
                </div>
                {(p.epd_amount > 0 || p.bgc_amount > 0) && (
                  <div style={{ display: 'flex', gap: 12, fontSize: 11, marginBottom: 4 }}>
                    {p.epd_amount > 0 && (
                      <span style={{ color: 'var(--muted)' }}>
                        EPD{p.epd_rate ? ` (${(p.epd_rate * 100).toFixed(0)}%)` : ''}: <strong style={{ color: 'var(--warning)' }}>-${Number(p.epd_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
                      </span>
                    )}
                    {p.bgc_amount > 0 && (
                      <span style={{ color: 'var(--muted)' }}>
                        BGC: <strong style={{ color: 'var(--warning)' }}>-${Number(p.bgc_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
                      </span>
                    )}
                  </div>
                )}
                <div style={{ color: 'var(--muted)', fontSize: 11 }}>
                  {p.payment_date} {p.batch_ref && `· Batch: ${p.batch_ref}`}
                </div>
                <NoteText text={p.notes} />
                {user?.role === 'admin' && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => setVerification(p.id, 'verified')}>✓ Verify</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setVerification(p.id, 'disputed')}>✗ Dispute</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => startEditPayment(p)}>✎ Edit</button>
                  </div>
                )}
              </div>
            ))}

            <button className="btn btn-secondary" style={{ width: '100%', marginTop: 14 }} onClick={() => setShowPayForm(true)}>
              + Record Payment
            </button>

            {showPayForm && (
              <div style={{ marginTop: 16, padding: 12, background: 'var(--surface)', borderRadius: 'var(--radius)' }}>
                <h5 style={{ marginBottom: 10, fontSize: 12, fontWeight: 700 }}>{editingPaymentId ? 'Edit Payment' : 'Record Payment'}</h5>
                <div className="form-field" style={{ marginBottom: 10 }}>
                  <label>Payment Date</label>
                  <input type="date" value={payForm.payment_date} onChange={e => setPayForm(f => ({ ...f, payment_date: e.target.value }))} />
                </div>
                <div className="form-field" style={{ marginBottom: 10 }}>
                  <label>Cash Amount</label>
                  <input type="number" step="0.01" value={payForm.cash_amount} onChange={e => setPayForm(f => ({ ...f, cash_amount: e.target.value }))} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                  <div className="form-field">
                    <label>EPD Rate (%)</label>
                    <input type="number" step="0.01" value={payForm.epd_rate} onChange={e => setPayForm(f => ({ ...f, epd_rate: e.target.value }))} />
                  </div>
                  <div className="form-field">
                    <label>EPD Amount</label>
                    <input type="number" step="0.01" value={payForm.epd_amount} onChange={e => setPayForm(f => ({ ...f, epd_amount: e.target.value }))} />
                  </div>
                </div>
                <div className="form-field" style={{ marginBottom: 10 }}>
                  <label>BGC Amount</label>
                  <input type="number" step="0.01" value={payForm.bgc_amount} onChange={e => setPayForm(f => ({ ...f, bgc_amount: e.target.value }))} />
                </div>
                <div className="form-field" style={{ marginBottom: 10 }}>
                  <label>Batch Ref</label>
                  <input value={payForm.batch_ref} onChange={e => setPayForm(f => ({ ...f, batch_ref: e.target.value }))} placeholder="e.g. 000000057725" />
                </div>
                <div className="form-field" style={{ marginBottom: 10 }}>
                  <label>Notes</label>
                  <input value={payForm.notes || ''} onChange={e => setPayForm(f => ({ ...f, notes: e.target.value }))} />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => { setShowPayForm(false); setEditingPaymentId(null); setPayForm(EMPTY_PAY_FORM); }}>Cancel</button>
                  <button className="btn btn-primary" style={{ flex: 1 }} onClick={savePayment} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
                </div>
              </div>
            )}

            <DocumentWallet entityType="ar_invoice" entityId={invoiceId} entityName={data?.invoice_number} />
            <AuditLog tableName="ar_invoices" recordId={invoiceId} />
          </div>
        )}

        {editForm && isEditing && (
          <div className="panel-body">
            {editError && <div style={{ background: '#fee', color: '#c33', padding: 10, borderRadius: 4, marginBottom: 12, fontSize: 12 }}>{editError}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
              <div className="form-field"><label>Invoice #</label><input type="text" value={editForm.invoice_number || ''} onChange={e => setEditForm(f => ({ ...f, invoice_number: e.target.value }))} /></div>
              <div className="form-field"><label>Invoice Date</label><input type="date" value={editForm.invoice_date || ''} onChange={e => setEditForm(f => ({ ...f, invoice_date: e.target.value }))} /></div>
              <div className="form-field"><label>Period Start</label><input type="date" value={editForm.period_start || ''} onChange={e => setEditForm(f => ({ ...f, period_start: e.target.value }))} /></div>
              <div className="form-field"><label>Period End</label><input type="date" value={editForm.period_end || ''} onChange={e => setEditForm(f => ({ ...f, period_end: e.target.value }))} /></div>
              <div className="form-field"><label>Hours Billed</label><input type="number" step="0.01" value={editForm.hours_billed || ''} onChange={e => setEditForm(f => ({ ...f, hours_billed: e.target.value }))} /></div>
              <div className="form-field"><label>Hours Approved</label><input type="number" step="0.01" value={editForm.hours || ''} onChange={e => setEditForm(f => ({ ...f, hours: e.target.value }))} /></div>
              <div className="form-field"><label>Rate ($/hr)</label><input type="number" step="0.01" value={editForm.rate || ''} onChange={e => setEditForm(f => ({ ...f, rate: e.target.value }))} /></div>
              <div className="form-field"><label>Amount</label><input type="number" step="0.01" value={editForm.amount || ''} onChange={e => setEditForm(f => ({ ...f, amount: e.target.value }))} /></div>
              <div className="form-field"><label>Due Date</label><input type="date" value={editForm.due_date || ''} onChange={e => setEditForm(f => ({ ...f, due_date: e.target.value }))} /></div>
              <div className="form-field">
                <label>Invoice Type</label>
                <select value={editForm.invoice_type || 'standard'} onChange={e => setEditForm(f => ({ ...f, invoice_type: e.target.value }))}>
                  <option value="standard">Standard</option>
                  <option value="passthrough">Allowance / Reimbursement</option>
                </select>
              </div>
              <div className="form-field"><label>Notes</label><textarea value={editForm.notes || ''} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} rows="3" /></div>
              {editForm.invoice_type === 'passthrough' && (
                <div className="form-field">
                  <label>Reference Note (prints on invoice)</label>
                  <input type="text" placeholder="e.g. Manager email attached, Phone bill attached" value={editForm.reference_note || ''} onChange={e => setEditForm(f => ({ ...f, reference_note: e.target.value }))} />
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn-secondary" onClick={cancelEdit} disabled={editSaving}>Cancel</button>
              <button className="btn btn-primary" onClick={saveEdit} disabled={editSaving}>{editSaving ? 'Saving…' : 'Save Changes'}</button>
            </div>
          </div>
        )}

        {!data && <div className="panel-body"><div style={{ textAlign: 'center', padding: 20, color: 'var(--muted)' }}>Loading…</div></div>}
      </div>
    </>
  );
}

function DetailRow({ label, value }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <span style={{ color: 'var(--muted)', flexShrink: 0 }}>{label}</span>
      <span style={{ fontWeight: 600, textAlign: 'right' }}>{value}</span>
    </div>
  );
}
